import React, { useState } from "react";
import t from "Utils/translation";
import WarningBanner from "Components/warningBanner/WarningBanner";
import Button from "Components/button/Button";
import ParametersTable from "Components/parametersTable/ParametersTable";
import cx from "classnames";
import Input from "Components/input/Input";
import { Icon, upload } from "Icons/index";
import s from "../../delivery.scss";
import QboxmailApi from "@/qboxmailApi";
import { alertError, alertSuccess } from "@/utils/alerts";
import Modal from "Components/smallModal/smallModal";
import isEmail from "validator/lib/isEmail";
const api = new QboxmailApi({ useCookie: true });

const downloadAsCSV = (domain, records) => {
  let csv = "dns_record_type,name,value\n";

  records.forEach(function (row) {
    csv += row.map((r) => `"${r}"`).join(",");
    csv += "\n";
  });

  const fileBlob = new Blob([csv], { type: "text/csv" });

  const hiddenElement = document.createElement("a");
  hiddenElement.href = URL.createObjectURL(fileBlob);
  hiddenElement.target = "_blank";

  hiddenElement.download = `delivery-${domain}-dns-config.csv`;
  hiddenElement.click();
};

function DeliveryDomainVerification({ domain, onUpdate }) {
  const [verificating, setVerificating] = useState(false);
  const [shareOpened, setShareOpened] = useState(false);
  const [spfStatus, setSpfStatus] = useState(domain.dns_verification_status === "verified" ? "valid" : null);
  const [dmarcStatus, setDmarcStatus] = useState(domain.dns_verification_status === "verified" ? "valid" : null);
  const [dkimTrStatus, setDkimTrStatus] = useState(domain.dns_verification_status === "verified" ? "valid" : null);
  const [dkimNlStatus, setDkimNlStatus] = useState(domain.dns_verification_status === "verified" ? "valid" : null);

  const [emailToShare, setEmailToShare] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [sharing, setSharing] = useState(false);

  const share = async (e) => {
    e.preventDefault();

    // validate isEmail
    if (!isEmail(emailToShare) || !emailToShare) {
      return setInvalidEmail(true);
    }

    setSharing(true);

    //eslint-disable-next-line
    const shared = await api.shareDeliveryDomainConfiguration(domain.code, { to: emailToShare }).catch((err) => null);

    if (!shared) {
      setSharing(false);
      return alertError(t("delivery.domains.verification.send.error"));
    }

    alertSuccess(
      t("delivery.domains.verification.send.success", null, {
        email: emailToShare,
      })
    );
    setEmailToShare("");
    setShareOpened(false);

    setSharing(false);
    setInvalidEmail(false);
  };

  const verify = async (e) => {
    e.preventDefault();

    setVerificating(true);
    const success = await api.validateDeliveryDomain(domain.code).catch(async (err) => {
      if (err.status === 422) {
        alertError(t("alert.deliveryDomain.verification.fail"));
        const body = await err.json();

        setDmarcStatus(body.errors.find((e) => e.field === "dmarc") ? "error" : "valid");
        setSpfStatus(body.errors.find((e) => e.field === "spf") ? "error" : "valid");
        setDkimTrStatus(body.errors.find((e) => e.field === "tr") ? "error" : "valid");
        setDkimNlStatus(body.errors.find((e) => e.field === "nl") ? "error" : "valid");
      }
      return false;
    });

    if (success) {
      if (onUpdate) await onUpdate();
      alertSuccess(t("alert.deliveryDomain.verification.success"));
      setSpfStatus("valid");
      setDkimTrStatus("valid");
      setDkimNlStatus("valid");
    }

    setVerificating(false);
  };

  return (
    <div className={s.dashboardContainer}>
      <div className={s.dashboardContent}>
        <div className={s.dashboardItem1}>
          <div className="settingTitle">{t("delivery.domains.verification.spf_dkim.title")}</div>
          <div className="sectionDesc">{t("delivery.domains.verification.spf_dkim.desc")}</div>
          <div className="mt-8">
            <Button docsKey="deliveryDomainsVerification" className="btnDocsFP" color="docs" sizeSmall>
              button.docs
            </Button>
          </div>
        </div>
        <div className={s.dashboardItem2}>
          {domain.dns_verification_status === "verified" ? (
            <WarningBanner color="green" info msg={t("delivery.domains.verification.verified")} />
          ) : (
            <WarningBanner msg={t("delivery.domains.verification.notVerified")} />
          )}

          {/* spf */}
          <ParametersTable
            className="mt-8"
            status={spfStatus}
            params={[
              { label: t("detailInfo.label.type"), value: "TXT" },
              { label: t("detailInfo.label.value"), value: domain.txt_spf_record, copy: true },
            ]}
            warningComponent={
              <WarningBanner info color="blue" msg={t("delivery.domains.verification.spf_dkim.spf.warningMessage")} />
            }
            label={t("delivery.domains.verification.spf_dkim.spf.label")}
          />

          {/* dkim */}
          <div className={cx("mt-8 text-sm", s.tableLabel)}>
            {t("delivery.domains.verification.spf_dkim.dkim.label")}
          </div>

          <div className="my-3">
            <WarningBanner info color="blue" msg={t("delivery.domains.verification.spf_dkim.dkim.warningMessage")} />
          </div>

          {domain.cname_verification_records.map((record) => {
            return (
              <ParametersTable
                key={record.name}
                status={record.type === "tr" ? dkimTrStatus : record.type === "nl" ? dkimNlStatus : null}
                params={[
                  { label: t("detailInfo.label.type"), value: "CNAME" },
                  { label: t("detailInfo.label.name"), value: record.name, copy: true },
                  { label: t("detailInfo.label.value"), value: record.value, copy: true },
                ]}
              />
            );
          })}

          {/* dmarc */}
          <ParametersTable
            className="mt-8"
            status={dmarcStatus}
            params={[
              { label: t("detailInfo.label.type"), value: "TXT" },
              { label: t("detailInfo.label.name"), value: `_dmarc.${domain.name}`, copy: true },
              { label: t("detailInfo.label.value"), value: domain.suggested_dmarc_record, copy: true },
            ]}
            warningComponent={
              <WarningBanner info color="blue" msg={t("delivery.domains.verification.spf_dkim.dmarc.warningMessage")} />
            }
            label={t("delivery.domains.verification.spf_dkim.dmarc.label")}
          />

          <div className={cx(s.shareLink, "mt-8")} onClick={() => setShareOpened(!shareOpened)}>
            <Icon svg={upload} color="accent" width="1rem" height="1rem" />
            <div>{t("delivery.domains.verification.spf_dkim.shareRecords")}</div>
          </div>

          <Modal
            title={t("delivery.domains.verification.spf_dkim.shareRecords")}
            opened={shareOpened}
            closeModal={() => setShareOpened(false)}
            withForm
          >
            <form onSubmit={share} className={s.shareForm}>
              <div className={s.shareModalBody}>
                <p>{t("delivery.domains.verification.share_email_desc")}</p>
                <Input
                  className="inputFull2"
                  name="email"
                  trim
                  label={t("delivery.domains.verification.share_email")}
                  placeholder={""}
                  value={emailToShare}
                  errors={invalidEmail ? [t("delivery.domains.verification.share_email_error")] : null}
                  onChange={(value) => setEmailToShare(value.email)}
                  type="email"
                  required
                />

                <div
                  className={cx(s.shareLink)}
                  onClick={() =>
                    downloadAsCSV(domain.name, [
                      ["TXT", "", domain.txt_spf_record],
                      ...domain.cname_verification_records.map((record) => ["CNAME", record.name, record.value]),
                    ])
                  }
                >
                  <Icon svg={upload} color="accent" width="1rem" height="1rem" />
                  <div>{t("delivery.domains.verification.share_params_download")}</div>
                </div>
              </div>

              <div className={s.shareModalFooter}>
                <Button type="button" color="text" onClick={() => setShareOpened(false)}>
                  button.close
                </Button>

                <Button loading={sharing} onClick={share} type="submit">
                  delivery.domains.verification.send
                </Button>
              </div>
            </form>
          </Modal>

          <div className={cx("mt-8", s.footer)}>
            <div></div>
            {/* {isLoggedUserAdmin && !domain.status && domain.approval_status !== "pending" ? (
              <Button className={s.skipBtn} onClick={skip} color="outline" loading={verificating}>
                button.skipDeliveryDns
              </Button>
            ) : (

            )} */}

            {domain.dns_verification_status !== "verified" && (
              <Button loading={verificating} onClick={verify} type="button">
                button.verify
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryDomainVerification;
